import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Typography,
    Chip,
    Avatar,
    FormHelperText,
    Tab,
    Tabs,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import './SectionForm.css';

const SectionForm = ({ open, onClose, onSubmit, initialData, availableContents = [], availableSeries = [], sections = [] }) => {
    const [formData, setFormData] = useState({
        title: '',
        keywords: [],
        chapters: [],
        index: 0,
        position: '',
        exhibitionType: 'landscape'
    });
    const [keywordInput, setKeywordInput] = useState('');
    const [contentSearch, setContentSearch] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (initialData) {
            setFormData({
                title: initialData.title || '',
                keywords: initialData.keywords || [],
                chapters: initialData.chapters || [],
                index: initialData.index || 0,
                position: initialData.position || (initialData.index !== undefined ? initialData.index + 1 : ''),
                exhibitionType: initialData.exhibitionType || 'landscape'
            });
        } else {
            setFormData({
                title: '',
                keywords: [],
                chapters: [],
                index: 0,
                position: '',
                exhibitionType: 'landscape'
            });
        }
    }, [initialData, open]);

    // Filtrar conteúdos e séries baseado na busca
    const filteredContents = availableContents.filter(content =>
        content.title?.toLowerCase().includes(contentSearch.toLowerCase()) ||
        content.description?.toLowerCase().includes(contentSearch.toLowerCase())
    );

    const filteredSeries = availableSeries.filter(serie =>
        serie.title?.toLowerCase().includes(contentSearch.toLowerCase()) ||
        serie.description?.toLowerCase().includes(contentSearch.toLowerCase())
    );

    // Gera as opções de posição disponíveis (exclusivas)
    const usedPositions = sections
        .filter(s => !initialData || s.id !== initialData.id)
        .map(s => (s.index !== undefined ? s.index + 1 : null))
        .filter(pos => pos !== null);
    const maxPosition = sections.length + (initialData && initialData.id ? 0 : 1);
    const availablePositions = [];
    for (let i = 1; i <= maxPosition; i++) {
        if (!usedPositions.includes(i)) availablePositions.push(i);
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleKeywordInputChange = (e) => {
        setKeywordInput(e.target.value);
    };

    const handleKeywordKeyPress = (e) => {
        if (e.key === 'Enter' && keywordInput.trim()) {
            setFormData(prev => ({
                ...prev,
                keywords: [...prev.keywords, keywordInput.trim()]
            }));
            setKeywordInput('');
        }
    };

    const handleRemoveKeyword = (indexToRemove) => {
        setFormData(prev => ({
            ...prev,
            keywords: prev.keywords.filter((_, index) => index !== indexToRemove)
        }));
    };

    const moveItemUp = (index) => {
        if (index === 0) return; // Já está no topo
        
        setFormData(prev => {
            const newChapters = [...prev.chapters];
            
            // Trocar posições no array
            const temp = newChapters[index];
            newChapters[index] = newChapters[index - 1];
            newChapters[index - 1] = temp;
            
            // Atualizar índices para refletir a nova ordem
            newChapters.forEach((chapter, i) => {
                chapter.index = i + 1;
            });
            
            return {
                ...prev,
                chapters: newChapters
            };
        });
    };

    const moveItemDown = (index) => {
        if (index === formData.chapters.length - 1) return; // Já está embaixo
        
        setFormData(prev => {
            const newChapters = [...prev.chapters];
            
            // Trocar posições no array
            const temp = newChapters[index];
            newChapters[index] = newChapters[index + 1];
            newChapters[index + 1] = temp;
            
            // Atualizar índices para refletir a nova ordem
            newChapters.forEach((chapter, i) => {
                chapter.index = i + 1;
            });
            
            return {
                ...prev,
                chapters: newChapters
            };
        });
    };

    const handleItemClick = (item, type) => {
        const existingIndex = formData.chapters.findIndex(
            chapter => chapter.type === type &&
                (type === 'serie' ? chapter.serie_id === item.id : chapter.content_id === item.id)
        );

        if (existingIndex !== -1) {
            // Remove if already selected
            setFormData(prev => ({
                ...prev,
                chapters: prev.chapters.filter((_, index) => index !== existingIndex)
            }));
        } else {
            // Add new chapter with next index
            const nextIndex = formData.chapters.length + 1;
            const newChapter = type === 'serie'
                ? { type, serie_id: item.id, index: nextIndex }
                : { type, content_id: item.id, index: nextIndex };

            setFormData(prev => ({
                ...prev,
                chapters: [...prev.chapters, newChapter]
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.title) newErrors.title = 'Título é obrigatório';
        if (!formData.position) newErrors.position = 'Posição é obrigatória';
        if (formData.chapters.length === 0) newErrors.chapters = 'Selecione pelo menos um item';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        onSubmit(formData);
        onClose();
    };

    const isItemSelected = (item, type) => {
        const isSelected = formData.chapters.some(chapter =>
            chapter.type === type &&
            (type === 'serie' ? chapter.serie_id === item.id : chapter.content_id === item.id)
        );
        return isSelected;
    };

    const renderChaptersList = () => {
        const sortedChapters = [...formData.chapters].sort((a, b) => a.index - b.index);

        return sortedChapters.map((chapter, index) => {
            const item = chapter.type === 'serie'
                ? availableSeries.find(s => s.id === chapter.serie_id)
                : availableContents.find(c => c.id === chapter.content_id);

            if (!item) return null;

            return (
                <ListItem key={`${chapter.type}-${chapter.type === 'serie' ? chapter.serie_id : chapter.content_id}`}>
                    <Avatar
                        variant="rounded"
                        src={item.thumbnailUrl}
                        alt={item.title}
                        className="item-thumbnail"
                    />
                    <ListItemText
                        primary={item.title}
                        secondary={`${chapter.type === 'serie' ? 'Série' : 'Conteúdo'} - Capítulo ${chapter.index}`}
                        className="item-text"
                    />
                    <ListItemSecondaryAction>
                        <IconButton
                            edge="end"
                            onClick={() => moveItemUp(index)}
                            disabled={index === 0}
                            style={{ color: index === 0 ? '#666' : '#fff', marginRight: 4 }}
                            size="small"
                        >
                            <KeyboardArrowUp />
                        </IconButton>
                        <IconButton
                            edge="end"
                            onClick={() => moveItemDown(index)}
                            disabled={index === sortedChapters.length - 1}
                            style={{ color: index === sortedChapters.length - 1 ? '#666' : '#fff', marginRight: 4 }}
                            size="small"
                        >
                            <KeyboardArrowDown />
                        </IconButton>
                        <IconButton
                            edge="end"
                            onClick={() => handleItemClick(item, chapter.type)}
                            style={{ color: '#E31A13' }}
                        >
                            <Delete />
                        </IconButton>
                    </ListItemSecondaryAction>
                </ListItem>
            );
        });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            className="section-form-dialog"
        >
            <DialogTitle>
                {initialData ? 'Editar Seção' : 'Nova Seção'}
            </DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <Box className="form-grid">
                        <TextField
                            name="title"
                            label="Título"
                            value={formData.title || ''}
                            onChange={handleChange}
                            fullWidth
                            required
                            error={!!errors.title}
                            helperText={errors.title}
                        />

                        <FormControl fullWidth>
                            <InputLabel>Tipo de Apresentação</InputLabel>
                            <Select
                                name="exhibitionType"
                                value={formData.exhibitionType || 'landscape'}
                                onChange={handleChange}
                                label="Visual Format"
                            >
                                <MenuItem value="portrait">Portrait</MenuItem>
                                <MenuItem value="square">Square</MenuItem>
                                <MenuItem value="grid">Grid</MenuItem>
                                <MenuItem value="landscape">Landscape</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Campo hidden para posição - usado apenas na validação */}
                        <input
                            type="hidden"
                            name="position"
                            value={formData.position || ''}
                        />

                        <Box className="keywords-section">
                            <Typography variant="subtitle1">Keywords</Typography>
                            <TextField
                                value={keywordInput}
                                onChange={handleKeywordInputChange}
                                onKeyPress={handleKeywordKeyPress}
                                placeholder="Digite e pressione Enter"
                                fullWidth
                                margin="normal"
                            />
                            <Box className="keywords-list">
                                {formData.keywords.map((keyword, index) => (
                                    <Chip
                                        key={index}
                                        label={keyword}
                                        onDelete={() => handleRemoveKeyword(index)}
                                        className="keyword-chip"
                                    />
                                ))}
                            </Box>
                        </Box>

                        <Box className="content-section">
                            <Typography variant="h6" className="section-title">
                                Conteúdo da Seção
                            </Typography>
                            {errors.chapters && (
                                <FormHelperText error>{errors.chapters}</FormHelperText>
                            )}

                            <Box className="content-container">
                                <Box className="available-items">
                                    <TextField
                                        label="Buscar"
                                        value={contentSearch}
                                        onChange={(e) => setContentSearch(e.target.value)}
                                        fullWidth
                                        margin="normal"
                                    />
                                    <Tabs
                                        value={activeTab}
                                        onChange={(e, newValue) => setActiveTab(newValue)}
                                        className="content-tabs"
                                    >
                                        <Tab label="Conteúdos" />
                                        <Tab label="Séries" />
                                    </Tabs>
                                    <List className="items-list">
                                        {activeTab === 0 ? (
                                            filteredContents.map((content) => (
                                                <ListItem
                                                    key={content.id}
                                                    onClick={() => handleItemClick(content, 'content')}
                                                    selected={isItemSelected(content, 'content')}
                                                >
                                                    <Avatar
                                                        variant="rounded"
                                                        src={content.thumbnailUrl}
                                                        alt={content.title}
                                                        className="item-thumbnail"
                                                    />
                                                    <ListItemText
                                                        primary={content.title}
                                                        secondary={content.description}
                                                        className="item-text"
                                                    />
                                                </ListItem>
                                            ))
                                        ) : (
                                            filteredSeries.map((serie) => (
                                                <ListItem
                                                    key={serie.id}
                                                    onClick={() => handleItemClick(serie, 'serie')}
                                                    selected={isItemSelected(serie, 'serie')}
                                                >
                                                    <Avatar
                                                        variant="rounded"
                                                        src={serie.thumbnailUrl}
                                                        alt={serie.title}
                                                        className="item-thumbnail"
                                                    />
                                                    <ListItemText
                                                        primary={serie.title}
                                                        secondary={serie.description}
                                                        className="item-text"
                                                    />
                                                </ListItem>
                                            ))
                                        )}
                                    </List>
                                </Box>

                                <Box className="selected-items">
                                    <Typography variant="subtitle1">
                                        Itens Selecionados ({formData.chapters.length})
                                    </Typography>
                                    <List className="items-list">
                                        {renderChaptersList()}
                                    </List>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} color="inherit">
                        Cancelar
                    </Button>
                    <Button type="submit" variant="contained" color="primary">
                        {initialData ? 'Salvar' : 'Criar'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default SectionForm; 